/**
 * @typedef {Object} Availability
 * @property {string} [firstAvailable]
 * @property {string} [lastAvailable]
 * @property {boolean} [unavailableShipping]
 * @property {string} [cityExclusive]
 */

/**
 * @typedef {Object} Zone
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {string} state
 * @property {string} color
 * @property {boolean} isPickup
 */

/**
 * @typedef {Object} InventoryDetails
 * @property {number} inventory_item_id
 * @property {number} location_id
 * @property {number} available
 * @property {string} state
 * @property {string} location
 * @property {number} variant_id
 * @property {number} product_id
 * @property {string} variant_title
 */

class Bundles {
  /**
   * Initialize the Bundles component
   */
  constructor() {
    // Check if Shopify is defined, if not, define it as an empty object
    if (typeof Shopify === "undefined") {
      window.Shopify = {}
    }

    const customData = Shopify.customData.bundles || {}

    /** @type {number} */
    this.suggestedPairingId = customData.suggestedPairingId

    /**
     * Whether or not pairing is preselected (gift set page)
     * @type {boolean}
     */
    this.pairingSelected = customData.pairingSelected

    /**
     * An array containing the product IDs of the current bundles menu
     * @type {number[]}
     */
    this.currentBundleIds = customData.currentBundleIds

    /**
     * Whether or not product has a suggested pairing (gift set)
     * @type {boolean}
     */
    this.hasSuggestedPairing = customData.hasSuggestedPairing

    // Bundle selection state
    this.selectedBundle = {
      id: Shopify.customData.bundleData?.suggestedPairingVariantId,
      qty: 1,
      properties: {
        _f4d_id: null,
      },
    }

    this.pairingTitle = null
    this.pairingPrice = null
    this.selectedBundleId = null
    this.previousBundleId = null
    this.previousBundlesInZone = []
    /** @type {InventoryDetails[]} */
    this.limitedAvailabilityBundles = []
    this.isDesktop = window.innerWidth > 960
    this.initDropdown = true
    /** @type {Availability|null} */
    this.availability = null
    /** @type {Zone|null} */
    this.selectedZone = null
    this.bundleUnavailableMessage = ""
    this.bundleSelected = false

    // DOM elements
    /** @type {HTMLElement|null} */
    this.pairingErrorMsg = document.getElementById("pairing-error")
    /** @type {HTMLElement|null} */
    this.overallCheckbox = document.getElementById("overall-checkbox")
    /** @type {HTMLElement|null} */
    this.availabilityMessage = document.getElementById("availability-message")

    this.build()
  }

  /**
   * Initialize the component
   */
  build() {
    this.addListeners()
    // Default dropdown to open on mobile
    // if (!this.isDesktop) {
    //   this.toggleBundleDropdown();
    // }
  }

  /**
   * Handle sold out bundles
   */
  handleSoldOut() {
    if (!this.selectedZone) return

    const unavailableShipping = this.availability?.unavailableShipping
    const cityExclusive = this.availability?.cityExclusive
    const city = this.selectedZone.name.split(" ")[0]
    const bundleSoldOut = this.limitedAvailabilityBundles.find((bundle) => bundle.product_id === this.selectedBundleId)

    // Determine bundle unavailability message
    if (
      (unavailableShipping && this.selectedZone.description.includes("shipping")) ||
      (cityExclusive && cityExclusive !== city)
    ) {
      this.bundleUnavailableMessage = "Sorry, this bundle is unavailable in your area!"
    } else if (bundleSoldOut?.state === this.selectedZone.state) {
      this.bundleUnavailableMessage = "Sorry, we're out of stock of this bundle in your area!"
    } else {
      this.bundleUnavailableMessage = ""
    }

    if (this.bundleSelected) {
      this.bundleSelectorHandler()
    }
  }

  /**
   * Add event listeners
   */
  addListeners() {
    document.addEventListener("soldoutbundlesevent", (event) => {
      this.selectedZone = event.detail.selectedZone
      this.limitedAvailabilityBundles = event.detail.soldOutBundles || []

      if (this.selectedZone) {
        this.setLimitedAvailabilityMsg(true)
        this.toggleAvailable(this.selectedZone.state)
        this.handleSoldOut()
      } else if (this.limitedAvailabilityBundles.length) {
        this.setLimitedAvailabilityMsg(false)
      }

      if (this.initDropdown) {
        if (this.pairingSelected) {
          this.toggleBundle(this.suggestedPairingId)
        } else {
          this.toggleBundle(this.suggestedPairingId, true)
        }
        this.initDropdown = false
      }
    })

    // Overall checkbox click handler
    this.overallCheckbox?.addEventListener("click", (event) => {
      event.stopPropagation()
      if (this.selectedBundleId) {
        this.deselectBundle(this.selectedBundleId)
        this.selectedBundleId = null
      } else {
        this.selectBundle(this.previousBundleId)
      }
    })

    // Pairing dropdown click handler
    document.getElementById("pairing-dropdown")?.addEventListener("click", this.toggleBundleDropdown.bind(this))

    // Bundle checkbox click handlers
    const bundleCheckboxes = document.getElementsByClassName("bundle-checkbox")
    for (const bundleCheckboxEl of bundleCheckboxes) {
      bundleCheckboxEl.addEventListener("click", (e) => {
        const id = Number(e.target.closest(".bundle-checkbox").dataset.id)
        this.toggleBundle(id)
      })
    }
  }

  /**
   * Toggles the visibility of the pairing dropdown
   */
  toggleBundleDropdown() {
    const pairingDetailsContainer = document.getElementById("bundle-details")
    if (!pairingDetailsContainer) return

    if (pairingDetailsContainer.classList.contains("h-0")) {
      // Open dropdown
      pairingDetailsContainer.classList.remove("h-0")
      const content = pairingDetailsContainer.querySelector(".dropdown-content")
      if (content) content.classList.remove("-translate-y-full")

      document.getElementById("bundles-chev-down")?.classList.add("hidden")
      document.getElementById("bundles-chev-up")?.classList.remove("hidden")

      const moreOptions = document.getElementById("more-options")
      if (moreOptions) moreOptions.innerText = ""

      if (this.isDesktop) window.scrollTo(0, pairingDetailsContainer.scrollHeight + 100)
    } else {
      // Close dropdown
      const content = pairingDetailsContainer.querySelector(".dropdown-content")
      if (content) content.classList.add("-translate-y-full")

      setTimeout(() => {
        document.getElementById("bundles-chev-down")?.classList.remove("hidden")
        document.getElementById("bundles-chev-up")?.classList.add("hidden")
        pairingDetailsContainer.classList.add("h-0")

        const moreOptions = document.getElementById("more-options")
        if (moreOptions) moreOptions.innerText = "More Options "

      }, 150)
    }
  }

  /**
   * Toggles the selection state of a bundle
   * @param {number} id - The product ID of the bundle to toggle
   * @param {boolean} [reset] - Flag indicating whether to reset the selection
   */
  toggleBundle(id, reset) {
    const bundleEl = document.getElementById(`${id}-checkbox`)
    if (!bundleEl) return

    const availabilityMessageText = bundleEl.dataset.availabilityMsgTxt

    if (this.availabilityMessage) {
      if (availabilityMessageText?.length) {
        this.availabilityMessage.classList.remove("hidden")
        this.availabilityMessage.innerText = availabilityMessageText
      } else {
        this.availabilityMessage.classList.add("hidden")
      }
    }

    if (this.selectedBundleId !== id) {
      // Select new bundle
      if (this.selectedBundleId) this.deselectBundle(this.selectedBundleId)
      this.previousBundleId = id

      const availability = {
        firstAvailable: bundleEl.dataset.minDate,
        lastAvailable: bundleEl.dataset.maxDate,
        unavailableShipping: bundleEl.dataset.unavailableShipping === "true",
        cityExclusive: bundleEl.dataset.cityExclusive,
      }

      this.availability = availability
      this.selectBundle(
        id,
        Number(bundleEl.dataset.bundleVariantId),
        bundleEl.dataset.bundlePrice,
        bundleEl.dataset.bundleTitle,
        reset,
      )
    } else {
      // Deselect current bundle
      this.selectedBundleId = null
      this.bundleUnavailableMessage = ""
      this.deselectBundle(id)
    }
  }

  /**
   * Selects a bundle and updates related UI elements
   * @param {number} id - The product ID of the bundle to select
   * @param {number} [bundleVariantId] - The variant ID of the selected bundle
   * @param {string} [bundlePrice] - The price of the selected bundle
   * @param {string} [bundleTitle] - The title of the selected bundle
   * @param {boolean} [reset=false] - Flag indicating whether to reset the selection
   */
  selectBundle(id, bundleVariantId, bundlePrice, bundleTitle, reset = false) {
    if (this.pairingErrorMsg) {
      this.pairingErrorMsg.classList.add("hidden")
    }

    if (!reset) this.selectedBundleId = id

    this.pairingTitle = bundleTitle || this.pairingTitle
    this.pairingPrice = bundlePrice !== undefined || bundlePrice === 0 ? bundlePrice : this.pairingPrice

    if (bundleVariantId) this.selectedBundle.id = bundleVariantId

    const pairingTextTitle = document.getElementById("pairing-text-title")
    if (pairingTextTitle) {
      pairingTextTitle.innerText = !this.isDesktop ? `Add ${this.pairingTitle}` : `Pair With ${this.pairingTitle}`
    }

    if (this.overallCheckbox) {
      this.overallCheckbox.classList.remove("hidden")
      this.overallCheckbox.dataset.id = id
    }

    if (reset) return

    this.handleCheckboxes(id)
    this.handleSoldOut()
    this.bundleSelected = true
    this.bundleSelectorHandler()
  }

  /**
   * Deselects a bundle and updates related UI elements
   * @param {number} id - The product ID of the bundle to deselect
   */
  deselectBundle(id) {
    this.handleCheckboxes(id, false)
    this.bundleSelected = false
    this.bundleSelectorHandler()
  }

  /**
   * Handles the visual representation of checkboxes for a bundle
   * @param {number} id - The product ID of the bundle whose checkboxes to handle
   * @param {boolean} [check=true] - Flag indicating whether to check the checkboxes
   */
  handleCheckboxes(id, check = true) {
    const checkboxContainer = document.getElementById(`bundle-card-${id}`)
    if (!checkboxContainer || !this.overallCheckbox) return

    const pairingCheckmarks = checkboxContainer.querySelectorAll(".icon-done")
    const overallPairingCheckmark = this.overallCheckbox.getElementsByClassName("icon-done")[0]

    if (check) {
      if (overallPairingCheckmark) {
        overallPairingCheckmark.classList.remove("bg-white")
        overallPairingCheckmark.classList.add("bg-copper")
      }

      pairingCheckmarks.forEach((pairingCheckmark) => {
        pairingCheckmark.classList.remove("bg-white")
        pairingCheckmark.classList.add("bg-copper")
      })
    } else {
      pairingCheckmarks.forEach((pairingCheckmark) => {
        pairingCheckmark.classList.add("bg-white")
        pairingCheckmark.classList.remove("bg-copper")
      })

      if (overallPairingCheckmark) {
        overallPairingCheckmark.classList.add("bg-white")
        overallPairingCheckmark.classList.remove("bg-copper")
      }
    }
  }

  /**
   * Dispatches a custom event with bundle selection data
   */
  bundleSelectorHandler() {
    const isSuggestedPairing = this.hasSuggestedPairing ? this.selectedBundleId === this.suggestedPairingId : false

    const pairingSelector = new CustomEvent("goodpairingselector", {
      detail: {
        goodsItem: this.bundleSelected ? this.selectedBundle : null,
        includePairing: this.bundleSelected,
        pairingPrice: this.pairingPrice,
        pairingTitle: this.pairingTitle,
        isSuggestedPairing,
        firstAvailable: this.availability?.firstAvailable,
        lastAvailable: this.availability?.lastAvailable,
        bundleUnavailableMessage: this.bundleUnavailableMessage,
      },
    })

    document.dispatchEvent(pairingSelector)
  }

  /**
   * Sets or resets the limited availability message for bundles
   * @param {boolean} reset - Flag indicating whether to reset the message
   */
  setLimitedAvailabilityMsg(reset) {
    const bundleDropdown = document.querySelector(".dropdown-content")
    if (!bundleDropdown) return

    const limitedBundleIds = this.limitedAvailabilityBundles.map((bundle) => bundle.product_id)

    for (const bundleEl of bundleDropdown.children) {
      const productId = Number(bundleEl.id.replace("bundle-card-", ""))

      if (limitedBundleIds.includes(productId)) {
        const limitedAvailabilitySpan = bundleEl.querySelector(".limited-availability")

        if (limitedAvailabilitySpan) {
          if (reset) {
            limitedAvailabilitySpan.classList.replace("inline-block", "hidden")
            limitedAvailabilitySpan.innerText = ""
          } else {
            limitedAvailabilitySpan.classList.replace("hidden", "inline-block")
            limitedAvailabilitySpan.innerText = "Limited Availability.\nEnter zip for details."
          }
        }
      }
    }
  }

  /**
   * Toggles the availability of bundles based on the selected zone state
   * @param {string} selectedZoneState - The selected zone state
   */
  toggleAvailable(selectedZoneState) {
    if (this.pairingErrorMsg) {
      this.pairingErrorMsg.classList.add("hidden")
    }

    if (this.limitedAvailabilityBundles?.length) {
      // Reset previously marked bundles
      if (this.previousBundlesInZone.length) {
        for (const id of this.previousBundlesInZone) {
          this.setAvailability(id, true)
        }
        this.previousBundlesInZone = []
      }

      // Mark bundles as unavailable in the current zone
      const limitedBundlesInZone = this.limitedAvailabilityBundles.filter(
        (bundle) => bundle.state === selectedZoneState,
      )

      for (const bundle of limitedBundlesInZone) {
        const id = bundle.product_id
        // If bundle doesn't exist on page then there's no availability to be set
        if (!document.getElementById(`${id}-checkbox`)) continue

        this.previousBundlesInZone.push(id)
        this.setAvailability(id, false)
      }
    }
  }

  /**
   * Sets or resets the availability status for a bundle
   * @param {number} id - The ID of the bundle to set availability for
   * @param {boolean} reset - Flag indicating whether to reset the availability status
   */
  setAvailability(id, reset) {
    const soldOutPairingTitle = document.getElementById(`${id}-title`)
    const soldOutPairingPrice = document.getElementById(`${id}-price`)
    const soldOutSpan = document.getElementById(`${id}-soldout`)
    const bundleCard = document.getElementById(`bundle-card-${id}`)

    if (!soldOutPairingTitle || !soldOutPairingPrice || !soldOutSpan || !bundleCard) return

    if (reset) {
      bundleCard.classList.replace("order-2", "order-1")
      soldOutPairingTitle.classList.replace("text-gray-400", "text-black")
      soldOutPairingPrice.classList.remove("hidden")
      soldOutSpan.classList.remove("desktop:inline-block", "flex")
      soldOutSpan.classList.add("hidden")
      return
    }

    bundleCard.classList.replace("order-1", "order-2")
    soldOutPairingTitle.classList.replace("text-black", "text-gray-400")
    soldOutPairingPrice.classList.add("hidden")
    soldOutSpan.classList.add("desktop:inline-block", "flex")
    soldOutSpan.classList.remove("hidden")
    soldOutSpan.innerText = " - Sold Out"
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new Bundles()
})
